import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTours } from "@/hooks/useTours";
import { filterTours, sortToursWithPriority } from "@/lib/tourUtils";
import { ComparisonTable } from "@/components/ComparisonTable";
import { FeedbackDialog } from "@/components/FeedbackDialog";
import { emptyFilters, type FilterState } from "@/types/tour";
import { Loader2, Settings, DollarSign, Map, Sailboat } from "lucide-react";

export default function Index() {
  const { data: tours, isLoading } = useTours();
  const [filters, setFilters] = useState<FilterState>(emptyFilters);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.href = "https://topcarib.com/";
  }, []);

  const filtered = tours ? sortToursWithPriority(filterTours(tours, filters)) : [];

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="bg-card border-b border-border px-6 lg:px-10 py-3 flex items-center justify-between z-50">
        <Link to="/" className="flex items-center">
          <span className="font-display text-xl font-extrabold text-warning tracking-tight">Top</span>
          <span className="font-display text-xl font-extrabold text-primary tracking-tight">Carib</span>
        </Link>
        <div className="flex items-center gap-5">
          <Link
            to="/aruba/things-to-do"
            className="text-sm font-medium text-foreground hover:text-primary transition-colors"
          >
            Things to do
          </Link>
          <Link to="/admin" className="text-muted-foreground hover:text-foreground transition-colors">
            <Settings className="w-4.5 h-4.5" />
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <header className="hero-gradient-bg relative overflow-hidden px-6 lg:px-10 py-9 md:py-12 text-center">
        {/* Floating blobs */}
        <div className="hero-blob hero-blob-1" />
        <div className="hero-blob hero-blob-2" />
        <div className="hero-blob hero-blob-3" />

        <div className="relative z-10">
          <h1 className="font-display text-3xl md:text-5xl font-extrabold text-foreground leading-tight">
            Most excursions look identical.
          </h1>
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-5 mt-5 text-foreground font-body text-sm md:text-base">
            <span className="flex items-center gap-1.5">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary flex-shrink-0">
                <Sailboat className="w-3.5 h-3.5 text-primary-foreground" />
              </span>
              Similar packages
            </span>
            <span className="flex items-center gap-1.5">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary flex-shrink-0">
                <DollarSign className="w-3.5 h-3.5 text-primary-foreground" />
              </span>
              Similar prices
            </span>
            <span className="flex items-center gap-1.5">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary flex-shrink-0">
                <Map className="w-3.5 h-3.5 text-primary-foreground" />
              </span>
              Similar itineraries
            </span>
          </div>
          <p className="text-foreground font-body font-bold mt-7 max-w-3xl mx-auto text-base md:text-lg leading-relaxed">
            What makes or breaks the experience is in the details.
          </p>
          <p className="text-foreground font-body mt-2 max-w-3xl md:max-w-4xl mx-auto text-sm md:text-base leading-relaxed">
            So we analyzed thousands of reviews to reveal what makes each tour unique in an easy-to-compare format.
          </p>

          {/* 1-2-3 Steps */}
          <div className="flex flex-col items-start gap-4 mt-8 max-w-xl mx-auto">
            <div className="flex items-start gap-3">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground font-display font-extrabold text-xs flex-shrink-0">
                1
              </span>
              <span className="text-foreground font-body text-sm md:text-base text-left">
                Use the filters on the left side to find the excursion that fits your style
              </span>
            </div>
            <div className="flex items-start gap-3">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground font-display font-extrabold text-xs flex-shrink-0">
                2
              </span>
              <span className="text-foreground font-body text-sm md:text-base text-left">
                Make your final choice based on what people actually say in the reviews
              </span>
            </div>
            <div className="flex items-start gap-3">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground font-display font-extrabold text-xs flex-shrink-0">
                3
              </span>
              <span className="text-foreground font-body text-sm md:text-base text-left">
                Book on one of the booking platforms for the lowest price
              </span>
            </div>
          </div>

          <p className="text-foreground/70 font-body font-bold mt-8 max-w-4xl mx-auto text-[0.65rem] md:text-xs">
            This is a pilot project focusing only on boat tours in Aruba.
          </p>
          <p className="text-foreground/70 font-body mt-2 max-w-4xl mx-auto text-[0.65rem] md:text-xs">
            We would love to hear what you think!{" "}
            <button
              onClick={() => setFeedbackOpen(true)}
              className="text-primary font-medium underline hover:opacity-80 transition-opacity cursor-pointer"
            >
              Send feedback
            </button>
          </p>
        </div>
      </header>

      {/* Main */}
      <main className="px-2 lg:px-6 py-8 pb-0">
        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <>
            <ComparisonTable tours={filtered} allTours={tours || []} filters={filters} onFiltersChange={setFilters} />
          </>
        )}
      </main>

      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </div>
  );
}
